import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { StoreFinderComponentDef } from '@df/catalog-ui/store/finder/store-finder.component.def';
import { IStoreData, IStoreDataV3, Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { App } from '@df/core/app';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { NICmsContentField } from '@icms/core/content';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { Nexus } from '@tomandco/nexus-api-types';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import { IconButtonComponent } from '../../../common/buttons/icon-button/icon-button.component';
import { FormFieldComponent } from '../../../common/form/field/form-field.component';
import { FormMuiFieldComponent } from '../../../common/form/mui-field/form-mui-field.component';
import { GoogleMapComponent } from '../../../common/google-map/google-map.component';
import { ResultComponent } from '../../../common/result/result.component';
import { StickyFreeComponent } from '../../../common/sticky-free/sticky-free.component';
import { PageStoreFinderDetailsComponent } from './details/page-store-finder-details.component';
import { PageStoreFinderStoreComponent } from './store/page-store-finder-store.component';

export namespace NPageStoreFinderPageType {
    /**
     * Store finder page type id
     */
    export const typeId = 'storeFinder';

    /**
     * Store finder page type data
     */
    export interface IPageTypeData {
        /**
         * Searcher page title
         */
        title?: string;
        /**
         * Popular searches section title
         */
        popularSearchesTitle?: string;
        /**
         * List of suggested cities
         */
        popularSearches?: IPopularSearch[];
    }

    export interface IPopularSearch {
        /**
         * Country code
         */
        country: string;

        /**
         * Locations list
         */
        locations: IPopularSearchLocation[];
    }
    export interface IPopularSearchLocation {
        /**
         * City name
         */
        name: string;
    }
}

function getCountriesSorted(app: App): Nexus.Lib.General.Country[] {
    const allCountries = app.apiConfigService.countries;
    const validCountryIds = uniq(app.resources.store.getAllExisting().map(i => i.data?.country_id));
    const storeCountry = app.multisite.current.country;
    const storeCountries = app.multisite.current.countries;
    return sortBy(
        // only matching countries
        allCountries?.filter(i => validCountryIds.includes(i.code)),
        [
            i => i.name,
            i => {
                if (storeCountry === i.code) {
                    return 10;
                }
                if (storeCountries?.includes(i.code)) {
                    return 5;
                }
                return 1;
            }
        ]
    );
}

@Component({
    selector: 'page-store-finder',
    templateUrl: 'page-store-finder.html',
    styleUrl: 'page-store-finder.scss',
    imports: [
        StickyFreeComponent,
        NgTemplateOutlet,
        ToggleClassDirective,
        TranslatePipe,
        FormFieldComponent,
        FormMuiFieldComponent,
        OrderByPipe,
        FormsModule,
        IconButtonComponent,
        ResultComponent,
        ShellComponent,
        GoogleMapComponent,
        ICmsClientModule,
        PageStoreFinderStoreComponent,
        PageStoreFinderDetailsComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsPageType({
    id: NPageStoreFinderPageType.typeId,
    title: 'Store Finder',
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            title: 'Page title',
            key: 'title',
            default: 'Find a store',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            title: 'Popular searches section title',
            key: 'popularSearchesTitle',
            default: 'Suggested cities'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'popularSearches',
            title: 'Popular searches',
            itemName: 'Locations set',
            itemNameField: 'country',
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Country',
                    key: 'country',
                    required: true,
                    options: () => {
                        return getCountriesSorted(window.app).map(i => ({ key: i.code, title: i.name }));
                    }
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'locations',
                    title: 'Location',
                    itemName: 'Location',
                    itemNameField: 'name',
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.text,
                            title: 'Location name',
                            key: 'name',
                            required: true
                        }
                    ]
                }
            ]
        }
    ]
})
export class PageStoreFinderComponent extends StoreFinderComponentDef implements OnInit {
    /**
     * List of countries you may select location from
     */
    countries: Nexus.Lib.General.Country[] = [];

    /**
     * Skip default geolocation search
     */
    override noGeolocationOnInit = true;

    /**
     * List of popular searches
     */
    popularSearches?: NPageStoreFinderPageType.IPopularSearchLocation[];

    /**
     * Currently previewed store
     */
    currentStore?: Store<IStoreDataV3>;

    /**
     * If store preview is visible
     */
    isPreviewVisible = false;

    /**
     * Stores verbosity
     */
    protected override loacateVerbosity = 3;

    /**
     * CMS data
     */
    @Input()
    content?: NPageStoreFinderPageType.IPageTypeData;

    /**
     * Dependencies
     */
    constructor(
        private dfModalService: DfModalService,
        private app: App,
        private elementHeightService: ElementHeightService,
        protected toggleService: ToggleService,
        private storeResource: StoreResource,
        ref: ChangeDetectorRef
    ) {
        super(ref);
    }

    /**
     * Init
     */
    override ngOnInit(): void {
        this.initCountries();
        this.address = this.hashStorageService.get('location');
        if (this.address) {
            this.search();
        }
        // NOTE: we do NOT want init from the parent here
    }

    /**
     * When country changes update all models as there is country filter on it
     */
    onCountryChange() {
        this.updatePopularSerches();
        this.updateAllFindableModels();
    }

    /**
     * Override parent method - we dont need to load all, we already have them
     */
    override loadAndShowAll(): void {
        this.updateAllFindableModels();
    }

    /**
     * Track store methods
     */
    storeTrackFn(index: number, store: Store): number {
        return store.id;
    }

    /**
     * Cancel search results
     */
    override reset(): void {
        this.address = '';
        this.lastSearchHash = '';
        this._isUserSearch = false;
        this.locationState = undefined;
        this.loadAndShowAll();
    }

    override onFindableModelsUpdate() {
        super.onFindableModelsUpdate();
        if (this.isUserSearch && this.client.sizeId < 2) {
            this.scrollBelowHeader();
            this.toggleService.open(['storeFinder', 'list']);
        } else {
            this.toggleService.open(['storeFinder', 'map']);
        }
    }

    /**
     * If results are visible
     */
    get isResultsView() {
        return this.isUserSearch;
    }

    /**
     * Scroll below header
     */
    private scrollBelowHeader(): void {
        const headerHeight = this.elementHeightService.getState('page-header').value || 0;
        window.scrollTo({ top: headerHeight, behavior: 'smooth' });
    }

    override search() {
        this.hashStorageService.set('country', this.country);
        super.search();
    }

    /**
     * Search for address
     */
    searchFor(address: string) {
        this.address = address;
        this.search();
    }

    /**
     * Preview store details
     * @param store
     */
    previewStore(store: Store) {
        if (this.client.isS || this.client.isM) {
            this.dfModalService.open(undefined, 'store-finder-details', undefined, { store });
        } else {
            this.currentStore = store;
            this.isPreviewVisible = true;
            this.detectChanges();
        }
    }

    /**
     * Close store preview
     */
    closePreview() {
        this.isPreviewVisible = false;
        this.detectChanges();
    }

    /**
     * Models visible on map
     */
    get mapModels(): Store<IStoreData>[] {
        return this.isPreviewVisible ? (this.currentStore ? [this.currentStore] : []) : this.findableModels || [];
    }

    /**
     * Prepares list of countries with stores
     */
    private initCountries() {
        this.countries = getCountriesSorted(this.app);
        const validCountryIds = this.storeResource.getAllExisting().map(i => (i?.data as IStoreDataV3)?.country_id);
        const preferedCountries = [this.hashStorageService.get('country'), this.app.multisite.current.country, validCountryIds[0]];
        this.country = preferedCountries.find(i => validCountryIds.includes(i));

        this.onCountryChange();
    }

    /**
     * Syncs popular searches for currently selected countr
     */
    private updatePopularSerches() {
        this.popularSearches = this.content?.popularSearches?.find(i => i.country === this.country)?.locations || [];
    }
}
